import { h } from 'preact';
import register from 'preact-custom-element';
import SelfServiceForm from './components/form/self-service';
import { SelfServiceFormProps } from './components/form/self-service/types';
import './scss/shared.scss';
import './scss/self-service.scss';

type Site = 'branston' | 'sarsons' | 'haywards' | 'mizkanJapan' | 'osu' | 'vinegarCompany';

const SiteConfig: Record<Site, SelfServiceFormProps> = {
    branston: {
        origin: 'Branston Website',
        products: {
            'Pickle': {
                '01t8d000000AeunAAC': 'Original Pickle (360g)',
                '01t8d000000AeupAAC': 'Original Pickle (520g)',
                '01t8d000000AeuqAAC': 'Original Pickle (720g)',
                '01t8d000000AeusAAC': 'Small Chunk Pickle (360g)',
                '01t8d000000AeuuAAC': 'Small Chunk Pickle (520g)',
                '01t8d000000AeuvAAC': 'Small Chunk Pickle (720g)',
                '01t8d000000AeuwAAC': 'Smooth Pickle',
            },
            'Squeezy Pickle': {
                '01t8d000000AeuxAAC': 'Small Chunk Squeezy Pickle',
                '01t8d000000AeuyAAC': 'Smooth Squeezy Pickle',
            },
            'Piccalilli': {
                '01t8d000000Aev6AAC': 'Piccalilli',
                '01t8d000000Aev7AAC': 'Small Chunk Piccalilli',
                '01tPz00000H9fRxIAJ': 'Chilli Piccalilli',
            },
            'Sauce': {
                '01t8d000000Aev5AAC': 'Rich & Fruity sauce',
            },
        },
        classNames: {
            heading: 'text-xl  sm:text-2xl  md:text-3xl  leading-none',
            button: {
                submit: 'button  button--wide  heading-text  text-md  weight-black  uppercase  theme--secondary',
                findAddress: 'button  heading-text  text-md  weight-black  uppercase  theme--secondary',
                close: 'button  button--wide  heading-text  text-md  weight-black  uppercase  theme--secondary',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/fair-processing-notice/',
            recipes: '/recipes/',
            recycling: '/faqs/#recycling',
            range: '/range/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
        images: {
            code: '/wp-content/themes/branston-2020/dist/images/contact/branston-lid.png',
        },
    },
    sarsons: {
        origin: 'Sarsons Website',
        products: {
            'Malt Vinegar': {
                '01t8d000000AevNAAS': 'Malt Vinegar (250ml)',
                '01t8d000000AevOAAS': 'Malt Vinegar (284ml)',
                '01t8d000000AevPAAS': 'Malt Vinegar (400ml)',
                '01t8d000000AevQAAS': 'Malt Vinegar (568ml)',
            },
            'Distilled Malt Vinegar': {
                '01t8d000000AevSAAS': 'Distilled Malt Vinegar (250ml)',
                '01t8d000000AevTAAS': 'Distilled Malt Vinegar (284ml)',
                '01t8d000000AevUAAS': 'Distilled Malt Vinegar (568ml)',
            },
            'Pickling Vinegar': {
                '01t8d000000AevRAAS': 'Classic Malt Pickling Vinegar',
                '01t8d000000AevVAAS': 'Distilled Malt Pickling Vinegar',
            },
            'Pickled Vegetables': {
                '01tPz000003x4CMIAY': 'Sweet Onions',
                '01tPz000003x3HuIAI': 'Silverskin Onions',
                '01tPz000003x5mjIAA': 'Mixed Pickle',
                '01tPz000003x5unIAA': 'Red Cabbage',
                '01tPz000003x5rZIAQ': 'Hot & Spicy Onions',
            },
            'Sauce': {
                '01t8d000000AevWAAS': 'Worcester Sauce',
                '01t8d000000AevXAAS': 'Gravy Browning Sauce',
            },
        },
        classNames: {
            heading: 'font-veneer  caps',
            button: {
                submit: 'button  button--light  font-veneer  caps',
                findAddress: 'button  button--light  font-veneer  caps',
                close: 'button  button--light  font-veneer  caps',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/privacy/',
            recipes: '/recipes/',
            recycling: '/how-to/#recycling',
            range: '/products/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
        images: {
            code: '/wp-content/themes/sarsons/images/sarsons-lid.png',
        },
    },
    haywards: {
        origin: 'Haywards Website',
        products: {
            '01t8d000000AevEAAS': 'Medium & Tangy Mixed Pickle',
            '01t8d000000Aev9AAC': 'Medium & Tangy Silverskin Onions',
            '01t8d000000AevHAAS': 'Sweet & Mild Red Cabbage',
            '01t8d000000Aev8AAC': 'Sweet & Mild Silverskin Onions',
            // '01t8d000000AevCAAS': 'Hot & Spicy Onions', // missing from latest data file
        },
        classNames: {
            heading: 'form__heading',
            button: {
                submit: 'button  button--large',
                findAddress: 'button  button--small',
                close: 'button  button--medium',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/fair-processing-notice/',
            recipes: '/recipes/',
            recycling: '/faqs/#recycling',
            range: '/range/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
        images: {
            code: '/wp-content/themes/haywards/images/haywards-lid.png',
        },
    },
    mizkanJapan: {
        origin: 'Mizkan Japan Website',
        products: {
            '01tPz00000JRZErIAP': 'Red Miso & Chilli Cooking Paste',
            '01tPz00000JRa1FIAT': 'White Miso & Sesame Cooking Paste',
            '01tPz00000JRa2rIAD': 'Yuzu Ponzu',
            '01tPz00000JRa9JIAT': 'Mirin-Style Sweet Seasoning',
            '01tPz00000JRa65IAD': 'Rice Vinegar',
        },
        classNames: {
            heading: 'font-heading size-3xl from-sm:size-4xl from-md:size-5xl from-lg:size-6xl leading-none uppercase',
            button: {
                submit: 'button button--blue size-base from-md:size-lg leading-snug tracking-wider',
                findAddress: 'button button--small size-base button--blue leading-snug tracking-wider',
                close: 'button button--blue size-base from-md:size-lg leading-snug tracking-wider',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/privacy/',
            recipes: '/recipes/',
            recycling: '/faqs/#recycling',
            range: '/range/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
    },
    osu: {
        origin: 'Osu Vinegar Website',
        products: {
            '01tPz00000JRZpxIAH': 'Apple Cider Vinegar with Apple Juice',
            '01t8d000000AevKAAS': 'Apple Cider Vinegar with Blueberry & Pomegranate Juice',
            '01tPz00000JRZunIAH': 'Apple Cider Vinegar with Lemon Juice & Hint of Ginger',
        },
        classNames: {
            heading: 'form__heading weight-bold size-2xl from-xs:size-3xl from-sm:size-4xl from-md:size-5xl from-lg:size-6xl from-xl:size-7xl leading-none tracking-wide uppercase text-balance',
            button: {
                submit: 'button button--wide border-gold weight-bold until-sm:size-sm from-lg:size-lg leading-tight uppercase tracking-widest',
                findAddress: 'button border-gold weight-bold until-sm:size-sm leading-tight uppercase tracking-widest',
                close: 'button border-gold weight-bold until-sm:size-sm leading-tight uppercase tracking-widest',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/privacy/',
            recipes: '/recipes/',
            recycling: '/faqs/#recycling',
            range: '/products/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
    },
    vinegarCompany: {
        origin: 'The Vinegar Company Website',
        products: {
            '01tPz00000O29xRIAR': 'White Vinegar',
        },
        classNames: {
            heading: 'font-heading size-2xl from-sm:size-3xl from-lg:size-4xl leading-none align-center uppercase',
            button: {
                submit: 'button button--wide weight-bold from-sm:size-lg fg-white bg-dark-green',
                findAddress: 'button weight-bold from-sm:size-lg fg-white bg-dark-green',
                close: 'button button--wide weight-bold from-sm:size-lg fg-white bg-dark-green',
            },
        },
        links: {
            faqs: '/faqs/',
            privacy: '/privacy/',
            recipes: '/#tips',
            recycling: '/faqs/',
            range: '/',
        },
        emailAddresses: {
            trade: 'trade.enquiries@mizkan.co.uk',
            hr: 'HR@mizkan.co.uk',
        },
        images: {
            code: '/wp-content/themes/vinegar-company/images/batch-code.jpg',
        },
    },
};

function Form({ site }: FormProps) {
    if (SiteConfig[site] === undefined) {
        throw new Error(`Invalid site config identifier "${site}"`);
    }

    return h(SelfServiceForm, SiteConfig[site]);
}

type FormProps = {
    site: Site,
};

register(Form, 'mizkan-self-service-form', ['site']);
